import { isProduction } from "@/app/utils/EnvironmentUtils";
import { AUTH_SCOPE } from "@/entry/constants";
import { LOGIN_JOURNEY, LOGIN_STAGE_RESULTS, LOGIN_STAGES, StructuredLog, trackClientLoginStage } from "@agentx/agentx-services";
import { ensureError } from "@uuip/unified-ui-platform-sdk";
import { MAX_EMAIL_LENGTH, NON_PROD_WEBEX_URL, TOKEN_REFRESH_TIMEOUT, VALID_EMAIL } from "./constants";
// @ts-expect-error - StorageAdapterLocalStorage doesn't have proper TypeScript declarations
import StorageAdapterLocalStorageModule from "@webex/storage-adapter-local-storage";
const WEBEX_TOKEN_KEY = "webex-token";
/**
 * Validates if the provided email is valid according to Webex backend requirements
 * @param email - The email string to validate
 * @returns boolean indicating if the email is valid
 */
export const isValidEmail = (email) => {
    return email.length <= MAX_EMAIL_LENGTH && VALID_EMAIL.test(email);
};
async function tokenRefreshCallback(webex, token, idBrokerUrl) {
    const tokenRefreshRequestOptions = {
        method: "POST",
        uri: `${idBrokerUrl}/idb/oauth2/v1/access_token`,
        form: {
            grant_type: "refresh_token",
            redirect_uri: "",
            refresh_token: token.refresh_token,
            self_contained_token: "true"
        },
        auth: {
            user: CLIENT_ID,
            pass: CLIENT_SECRET,
            sendImmediately: true
        },
        headers: {
            accept: "*/*"
        },
        shouldRefreshAccessToken: false
    };
    try {
        StructuredLog.debug("Login/WebEx/TokenRefresh/Start");
        const res = await webex.request(tokenRefreshRequestOptions);
        StructuredLog.debug("Login/WebEx/TokenRefresh/Complete", "", { statusCode: res.statusCode });
        return res.body;
    }
    catch (error) {
        const statusCode = error instanceof Error ? error.statusCode : undefined;
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred during token refresh";
        const errorDetails = error instanceof Error
            ? { statusCode, message: error.message }
            : { message: "Unknown error occurred during token refresh" };
        StructuredLog.error("Login/WebEx/TokenRefresh/Error", errorMessage, errorDetails);
        trackClientLoginStage({
            stage: LOGIN_STAGES.IDBROKER,
            result: LOGIN_STAGE_RESULTS.FAILURE,
            endpoint: LOGIN_JOURNEY.ENDPOINTS.IDBROKER_ACCESS_TOKEN,
            error_code: LOGIN_JOURNEY.ERROR_CODES.IDBROKER_ACCESS_TOKEN_FAILED,
            http_status: statusCode
        });
        if (statusCode && statusCode >= 400 && statusCode < 500) {
            window.dispatchEvent(new Event("js-sdk-ci-logout"));
            throw new Error(`Refresh token is invalid / expired, logging out the user. Status Code: ${statusCode}`);
        }
        return null;
    }
}
async function tokenRefreshHelper(webex, token) {
    const idBrokerUrl = webex.internal.services.get("idbroker");
    if (!idBrokerUrl) {
        StructuredLog.error("Login/WebEx/TokenRefresh/Error", "ID Broker service URL is not available in Webex instance");
        return null;
    }
    let newToken = await tokenRefreshCallback(webex, token, idBrokerUrl);
    let retryCount = 0;
    while (newToken === null && retryCount <= 5) {
        await new Promise((resolveRetry) => {
            setTimeout(resolveRetry, TOKEN_REFRESH_TIMEOUT);
        });
        retryCount++;
        newToken = await tokenRefreshCallback(webex, token, idBrokerUrl);
    }
    StructuredLog.trace("WebexTokenRefresh", "Retry Count : ", { retryCount });
    return newToken;
}
function getScope(config) {
    if (config.userToken) {
        return config.userToken.scope;
    }
    return config.useWebExAuth ? AUTH_SCOPE : "";
}
function getCredentialsRecord(webexConfig) {
    if (webexConfig.useWebExAuth) {
        return {};
    }
    return {
        ...(webexConfig.userToken && {
            credentials: {
                access_token: webexConfig.userToken.access_token,
                expires_in: webexConfig.userToken.expires_in,
                refresh_token: webexConfig.userToken.refresh_token,
                refresh_token_expires_in: webexConfig.userToken.refresh_token_expires_in,
                ...(webexConfig.userToken.scope ? { scope: getScope(webexConfig) } : {})
            }
        })
    };
}
function getCredentialsObject({ userToken, useWebExAuth }) {
    return {
        refreshCallback: (webex, token) => {
            return tokenRefreshHelper(webex, token);
        },
        clientType: "confidential",
        client_id: CLIENT_ID || "",
        client_secret: CLIENT_SECRET || "",
        redirect_uri: new URL(window.location.origin).href,
        scope: getScope({ userToken, useWebExAuth })
    };
}
const StorageAdapterLocalStorage = StorageAdapterLocalStorageModule;
const webexStorage = {
    storage: {
        boundedAdapter: new StorageAdapterLocalStorage(WEBEX_TOKEN_KEY)
    }
};
export function createWebexConfig({ userToken, useWebExAuth }) {
    StructuredLog.debug("Login/WebexConfig/CreateWebexConfig", "", {
        userToken: userToken ? true : false,
        useWebExAuth
    });
    StructuredLog.trace(`Login/WebexConfig/WebExInit`);
    const webexConfig = {
        config: {
            sdkType: "webex",
            ...(useWebExAuth ? { ...webexStorage } : {}),
            meetings: {
                reconnection: {
                    enabled: true
                },
                enableRtx: true
            },
            presence: { initializeWorker: true },
            mercury: {
                beforeLogoutOptionsCloseReason: "done (permanent)"
            },
            logger: {
                level: "debug"
            },
            services: {}, // Initialize services as empty object by default
            credentials: getCredentialsObject({ userToken, useWebExAuth })
        },
        ...getCredentialsRecord({ userToken, useWebExAuth })
    };
    if (!isProduction()) {
        StructuredLog.trace("Login/WebexConfig/Config=NonProd");
        webexConfig.config.services = {
            discovery: {
                u2c: NON_PROD_WEBEX_URL.U2C,
                hydra: NON_PROD_WEBEX_URL.HYDRA
            }
        };
    }
    return webexConfig;
}
export function saveIdentityUrls(ciUrls) {
    localStorage.setItem("identityUrls", JSON.stringify(ciUrls));
}
export function buildStateParam(emailHash) {
    const stateObj = {
        emailhash: emailHash
    };
    return btoa(JSON.stringify(stateObj));
}
export function getEmailHashFromState(state) {
    if (!state) {
        return null;
    }
    try {
        const parsedState = JSON.parse(atob(state));
        return typeof parsedState.emailhash === "string" ? parsedState.emailhash : null;
    }
    catch (error) {
        const validError = ensureError(error);
        StructuredLog.error("Login/LoginPage/ParseState/Error", "Failed to parse login state", {
            error: validError.message
        });
        return null;
    }
}
export async function cloneToken(webex, token, idBrokerUrl) {
    StructuredLog.debug("Login/LoginPage/CloneToken/Start");
    const newToken = await tokenRefreshCallback(webex, token, idBrokerUrl);
    StructuredLog.debug("Login/LoginPage/CloneToken/End");
    if (!newToken) {
        return null;
    }
    return {
        ...newToken
    };
}
export function clearWebexToken() {
    localStorage.removeItem(WEBEX_TOKEN_KEY);
}
